// src/utils/reporteGasGenerator.js

import { obtenerDatosEmpresa } from './tenantConfig';
import { calcularComisionPedido } from './reporteGasConsolidado';

// ==================== CONFIG FALLBACK ====================
export const SYSTEM_JJ_CONFIG = {
  nombre: 'SYSTEM JJ',
  rif: 'J-41023119-0',
  slogan: 'Servicio Técnico Especializado',
  direccion: 'C.C. LATIN CENTER PB LOCAL 01',
  ciudad: 'ACARIGUA - PORTUGUESA',
  telefono: '0416-7594723',
  whatsapp: '04167594723',
  email: 'soporte@systemjj.com',
  sitioWeb: 'www.systemjj.com'
};

const getEmpresa = () => {
  try {
    const datos = obtenerDatosEmpresa();
    return {
      nombre: datos.nombreComercial || datos.nombre || SYSTEM_JJ_CONFIG.nombre,
      rif: datos.rif || SYSTEM_JJ_CONFIG.rif,
      slogan: datos.slogan || SYSTEM_JJ_CONFIG.slogan,
      direccion: datos.direccion || SYSTEM_JJ_CONFIG.direccion,
      ciudad: datos.ciudad || SYSTEM_JJ_CONFIG.ciudad,
      telefono: datos.telefono1 || datos.telefono2 || SYSTEM_JJ_CONFIG.telefono,
      whatsapp: datos.whatsapp || datos.telefono1 || SYSTEM_JJ_CONFIG.whatsapp,
      email: datos.email || SYSTEM_JJ_CONFIG.email,
      sitioWeb: datos.sitioWeb || SYSTEM_JJ_CONFIG.sitioWeb,
      logoBase64: datos.logoBase64 || null,
    };
  } catch (err) {
    console.warn('⚠️ No se pudo obtener config del tenant, usando fallback:', err);
    return { ...SYSTEM_JJ_CONFIG, logoBase64: null };
  }
};

// ==================== UTILIDADES ====================
export const formatearFechaReporte = (fechaISO) => {
  if (!fechaISO) return 'No disponible';
  try {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-VE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return fechaISO; }
};

export const formatearFechaCorta = (fechaISO) => {
  if (!fechaISO) return 'No disponible';
  try {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-VE', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  } catch { return fechaISO; }
};

export const formatearNumero = (numero, decimales = 2) => {
  if (numero === null || numero === undefined) return '0.00';
  const num = Number(numero);
  if (isNaN(num)) return '0.00';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales
  }).format(num);
};

export const formatearMonedaUSD = (monto) => {
  if (monto === null || monto === undefined) return '$0.00';
  const num = Number(monto);
  if (isNaN(num)) return '$0.00';
  return `$${formatearNumero(num, 2)}`;
};

export const formatearMonedaBs = (valor) => {
  if (valor === null || valor === undefined) return 'Bs 0,00';
  const numero = Number(valor);
  if (isNaN(numero)) return 'Bs 0,00';
  return `Bs ${formatearNumero(numero, 2)}`;
};

// ==================== LOGO ====================
let LOGO_BASE64 = '';

export const cargarLogo = async () => {
  try {
    const empresa = getEmpresa();
    if (empresa.logoBase64) {
      const base64Limpio = empresa.logoBase64.includes(',')
        ? empresa.logoBase64.split(',')[1]
        : empresa.logoBase64;
      LOGO_BASE64 = base64Limpio;
      console.log('✅ Logo del tenant cargado (gas)');
      return LOGO_BASE64;
    }

    const response = await fetch('/jj.png');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        LOGO_BASE64 = reader.result.split(',')[1];
        resolve(LOGO_BASE64);
      };
      reader.onerror = () => { LOGO_BASE64 = ''; resolve(''); };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error cargando logo:', error);
    LOGO_BASE64 = '';
    return '';
  }
};

export const getLogoBase64 = () => LOGO_BASE64;

// ==================== NORMALIZACIÓN ====================
export const normalizarPedidoGas = (p) => {
  if (!p) return null;

  const detalles = Array.isArray(p.detalles) ? p.detalles : [];

  const cantidadTotal = detalles.reduce((s, d) => s + (Number(d.cantidad) || 0), 0);
  const kilosTotales = detalles.reduce(
    (s, d) => s + ((Number(d.kilogramos) || 0) * (Number(d.cantidad) || 0)), 0
  );
  const litrosTotales = detalles.reduce(
    (s, d) => s + ((Number(d.litros) || 0) * (Number(d.cantidad) || 0)), 0
  );

  return {
    idPedidoRecarga: p.idPedidoRecarga,
    numeroPedido: p.numeroPedido || `GAS-${p.idPedidoRecarga || '000'}`,
    fechaPedido: p.fechaPedido,
    fechaCompletado: p.fechaCompletado,
    nombreCliente: p.nombreCliente || 'Cliente sin nombre',
    cedulaCliente: p.cedulaCliente || '—',
    telefonoCliente: p.telefonoCliente || '—',
    nombreZona: p.nombreZona || 'Sin zona',
    direccionEntrega: p.direccionEntrega || '—',
    estado: p.estado || 'PENDIENTE',
    estadoPago: p.estadoPago || 'pendiente',
    tipoPago: p.tipoPago || 'contado',
    metodoPago: p.metodoPago || '',
    subtotalUsd: Number(p.subtotalUsd || 0),
    costoDeliveryUsd: Number(p.costoDeliveryUsd || 0),
    ivaUsd: Number(p.ivaUsd || 0),
    descuentoUsd: Number(p.descuentoUsd || 0),
    totalUsd: Number(p.totalUsd || 0),
    totalBs: Number(p.totalBs || 0),
    tasaBsUsd: Number(p.tasaBsUsd || 0),
    saldoPendienteUsd: Number(p.saldoPendienteUsd || 0),
    saldoPendienteBs: Number(p.saldoPendienteBs || 0),
    detalles,
    cantidadTotal,
    kilosTotales,
    litrosTotales,
    canalOrigen: p.canalOrigen || 'web'
  };
};

export const normalizarPedidosGas = (pedidos, pagos = []) => {
  if (!Array.isArray(pedidos)) return [];
  return pedidos.map(p => {
    const norm = normalizarPedidoGas(p);
    if (!norm) return null;

    const idPedido = norm.idPedidoRecarga ?? p.idPedido;
    const pagosDelPedido = (pagos || []).filter(pg => {
      const idPagoPedido = pg.idPedidoRecarga ?? pg.idPedido ?? pg.pedidoId ?? pg.idPedidoRecargaFK;
      if (idPagoPedido && idPedido && String(idPagoPedido) === String(idPedido)) return true;
      if (pg.numeroPedido && norm.numeroPedido) {
        if (String(pg.numeroPedido).trim() === String(norm.numeroPedido).trim()) return true;
      }
      return false;
    });

    for (const pg of pagosDelPedido) {
      const metodo = pg.metodoPago ?? pg.metodo_pago ?? pg.formaPago ?? pg.tipoPago;
      if (metodo && String(metodo).trim() !== '') {
        norm.metodoPago = String(metodo).toLowerCase().trim();
        break;
      }
    }

    return norm;
  }).filter(Boolean);
};

// ==================== BADGES ====================
export const getBadgeEstadoPedido = (estado) => {
  const map = {
    'PENDIENTE': { texto: 'PENDIENTE', clase: 'badge-pendiente' },
    'CONFIRMADO': { texto: 'CONFIRMADO', clase: 'badge-confirmado' },
    'ASIGNADO': { texto: 'ASIGNADO', clase: 'badge-asignado' },
    'EN_CAMINO': { texto: 'EN CAMINO', clase: 'badge-en-camino' },
    'EN_PROCESO': { texto: 'EN PROCESO', clase: 'badge-en-proceso' },
    'COMPLETADO': { texto: 'COMPLETADO', clase: 'badge-completado' },
    'CANCELADO': { texto: 'CANCELADO', clase: 'badge-cancelado' },
    'RECHAZADO': { texto: 'RECHAZADO', clase: 'badge-rechazado' }
  };
  return map[estado] || { texto: estado || 'DESCONOCIDO', clase: 'badge-default' };
};

export const getBadgeEstadoPago = (estado) => {
  const map = {
    'pendiente': { texto: 'PENDIENTE', clase: 'badge-pendiente' },
    'parcial': { texto: 'PARCIAL', clase: 'badge-parcial' },
    'pagado': { texto: 'PAGADO', clase: 'badge-completado' }
  };
  return map[estado] || { texto: estado || '—', clase: 'badge-default' };
};

export const getBadgeMetodoPago = (metodo) => {
  const m = String(metodo || '').toLowerCase().trim();
  const map = {
    'efectivo': { texto: 'Efectivo', clase: 'badge-metodo-efectivo' },
    'transferencia': { texto: 'Transferencia', clase: 'badge-metodo-transferencia' },
    'pago_movil': { texto: 'Pago Móvil', clase: 'badge-metodo-pago-movil' },
    'pago movil': { texto: 'Pago Móvil', clase: 'badge-metodo-pago-movil' },
    'pagomovil': { texto: 'Pago Móvil', clase: 'badge-metodo-pago-movil' },
    'zelle': { texto: 'Zelle', clase: 'badge-metodo-zelle' },
    'usdt': { texto: 'USDT', clase: 'badge-metodo-usdt' },
  };
  return map[m] || { texto: m ? m.toUpperCase() : 'Sin definir', clase: 'badge-default' };
};

// ==================== CÁLCULOS ====================
export const calcularEstadisticasGas = (pedidos) => {
  const norm = normalizarPedidosGas(pedidos);
  const total = norm.length;

  const pendientes = norm.filter(p => p.estado === 'PENDIENTE').length;
  const confirmados = norm.filter(p => p.estado === 'CONFIRMADO').length;
  const asignados = norm.filter(p => p.estado === 'ASIGNADO').length;
  const enCamino = norm.filter(p => p.estado === 'EN_CAMINO').length;
  const enProceso = norm.filter(p => p.estado === 'EN_PROCESO').length;
  const completados = norm.filter(p => p.estado === 'COMPLETADO').length;
  const cancelados = norm.filter(p => p.estado === 'CANCELADO' || p.estado === 'RECHAZADO').length;

  const totalFacturadoUsd = norm.filter(p => p.estado === 'COMPLETADO')
    .reduce((s, p) => s + p.totalUsd, 0);
  const totalFacturadoBs = norm.filter(p => p.estado === 'COMPLETADO')
    .reduce((s, p) => s + p.totalBs, 0);

  const totalKilos = norm.filter(p => p.estado === 'COMPLETADO')
    .reduce((s, p) => s + p.kilosTotales, 0);
  const totalLitros = norm.filter(p => p.estado === 'COMPLETADO')
    .reduce((s, p) => s + p.litrosTotales, 0);
  const totalCantidad = norm.filter(p => p.estado === 'COMPLETADO')
    .reduce((s, p) => s + p.cantidadTotal, 0);

  const totalDelivery = norm.filter(p => p.estado === 'COMPLETADO')
    .reduce((s, p) => s + p.costoDeliveryUsd, 0);

  const pendienteCobro = norm.reduce((s, p) => s + p.saldoPendienteUsd, 0);

  return {
    total,
    pendientes, confirmados, asignados, enCamino, enProceso, completados, cancelados,
    porcentajeCompletados: total > 0 ? Math.round((completados / total) * 100) : 0,
    porcentajePendientes: total > 0 ? Math.round((pendientes / total) * 100) : 0,
    totalFacturadoUsd,
    totalFacturadoBs,
    ticketPromedioUsd: completados > 0 ? totalFacturadoUsd / completados : 0,
    totalKilos,
    totalLitros,
    totalCantidad,
    totalDelivery,
    pendienteCobro
  };
};

// ==================== FILTROS ====================
export const filtrarPedidosGas = (pedidos, filtro, pagos = []) => {
  const norm = normalizarPedidosGas(pedidos, pagos);
  switch (filtro) {
    case 'todos': return norm;
    case 'pendientes': return norm.filter(p => p.estado === 'PENDIENTE');
    case 'en_proceso': return norm.filter(p => ['CONFIRMADO', 'ASIGNADO', 'EN_CAMINO', 'EN_PROCESO'].includes(p.estado));
    case 'completados': return norm.filter(p => p.estado === 'COMPLETADO');
    case 'cancelados': return norm.filter(p => ['CANCELADO', 'RECHAZADO'].includes(p.estado));
    case 'por_cobrar': return norm.filter(p => p.saldoPendienteUsd > 0);
    case 'pagados': return norm.filter(p => p.estadoPago === 'pagado');
    default: return norm;
  }
};

// ==================== GENERAR HTML ====================
export const generarHTMLReporteGas = (pedidos, titulo = null, filtro = 'todos', pagos = []) => {
  const empresa = getEmpresa();
  const pedidosNorm = filtrarPedidosGas(pedidos, filtro, pagos);
  const estadisticas = calcularEstadisticasGas(pedidosNorm);
  const fechaActual = new Date();

  const titulosPorFiltro = {
    'todos': 'REPORTE GENERAL DE PEDIDOS DE GAS',
    'pendientes': 'PEDIDOS PENDIENTES',
    'en_proceso': 'PEDIDOS EN PROCESO',
    'completados': 'PEDIDOS COMPLETADOS',
    'cancelados': 'PEDIDOS CANCELADOS',
    'por_cobrar': 'PEDIDOS POR COBRAR',
    'pagados': 'PEDIDOS PAGADOS'
  };
  const tituloReal = titulo || titulosPorFiltro[filtro] || 'REPORTE DE GAS';

  return `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${tituloReal} - ${empresa.nombre}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Arial', 'Helvetica', sans-serif; background: white; padding: 20px; }
        .reporte { max-width: 1400px; margin: 0 auto; }

        .header {
          padding: 15px 0;
          border-bottom: 2px solid #333;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .logo-area { display: flex; align-items: center; gap: 15px; }
        .logo-img { height: 50px; width: auto; object-fit: contain; }
        .empresa-nombre { font-size: 18px; font-weight: bold; color: #333; }
        .empresa-slogan { font-size: 11px; color: #666; margin-top: 3px; }
        .rif-info { text-align: right; font-size: 11px; line-height: 1.4; color: #555; }

        .titulo { padding: 10px 0; }
        .titulo h1 { font-size: 20px; font-weight: bold; color: #1a4d8c; }
        .fecha { font-size: 11px; color: #666; margin-top: 5px; }

        .stats-container {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          margin: 20px 0;
        }
        .stat-card {
          background: #f8f9fa;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 12px;
          text-align: center;
        }
        .stat-label {
          font-size: 10px;
          color: #666;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 5px;
          font-weight: bold;
        }
        .stat-value { font-size: 20px; font-weight: bold; color: #1a4d8c; }
        .stat-value.green { color: #2e7d32; }
        .stat-value.blue { color: #1565c0; }

        .tabla-container { overflow-x: auto; margin-top: 20px; }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
          border: 1px solid #ddd;
        }
        th {
          background: #f5f5f5;
          padding: 10px 8px;
          text-align: left;
          font-weight: bold;
          border-bottom: 2px solid #ddd;
          color: #333;
          font-size: 10px;
          text-transform: uppercase;
        }
        td {
          padding: 8px;
          border-bottom: 1px solid #eee;
          vertical-align: top;
        }
        tr:hover td { background: #fafafa; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }

        .badge-pendiente { background: #fff3cd; color: #856404; padding: 3px 8px; border-radius: 4px; font-size: 9px; font-weight: bold; }
        .badge-confirmado { background: #cfe2ff; color: #084298; padding: 3px 8px; border-radius: 4px; font-size: 9px; font-weight: bold; }
        .badge-asignado { background: #e2d5f7; color: #4a148c; padding: 3px 8px; border-radius: 4px; font-size: 9px; font-weight: bold; }
        .badge-en-camino { background: #ffe0b2; color: #e65100; padding: 3px 8px; border-radius: 4px; font-size: 9px; font-weight: bold; }
        .badge-en-proceso { background: #fff3cd; color: #b76e00; padding: 3px 8px; border-radius: 4px; font-size: 9px; font-weight: bold; }
        .badge-completado { background: #d1e7dd; color: #0f5132; padding: 3px 8px; border-radius: 4px; font-size: 9px; font-weight: bold; }
        .badge-cancelado { background: #f8d7da; color: #842029; padding: 3px 8px; border-radius: 4px; font-size: 9px; font-weight: bold; }
        .badge-rechazado { background: #f5c2c7; color: #842029; padding: 3px 8px; border-radius: 4px; font-size: 9px; font-weight: bold; }
        .badge-parcial { background: #fff3cd; color: #664d03; padding: 3px 8px; border-radius: 4px; font-size: 9px; font-weight: bold; }
        .badge-default { background: #e2e3e5; color: #383d41; padding: 3px 8px; border-radius: 4px; font-size: 9px; font-weight: bold; }

        .badge-metodo-efectivo { background: #d1e7dd; color: #0f5132; padding: 3px 8px; border-radius: 4px; font-size: 9px; font-weight: bold; }
        .badge-metodo-transferencia { background: #cfe2ff; color: #084298; padding: 3px 8px; border-radius: 4px; font-size: 9px; font-weight: bold; }
        .badge-metodo-pago-movil { background: #e2d5f7; color: #4a148c; padding: 3px 8px; border-radius: 4px; font-size: 9px; font-weight: bold; }
        .badge-metodo-zelle { background: #e0e7ff; color: #3730a3; padding: 3px 8px; border-radius: 4px; font-size: 9px; font-weight: bold; }
        .badge-metodo-usdt { background: #ccfbf1; color: #115e59; padding: 3px 8px; border-radius: 4px; font-size: 9px; font-weight: bold; }

        .footer {
          margin-top: 30px;
          padding-top: 15px;
          border-top: 1px solid #ddd;
          font-size: 10px;
          color: #666;
          text-align: center;
        }

        .controles {
          text-align: center;
          margin-top: 20px;
          padding: 15px;
          background: #f5f5f5;
          border-radius: 8px;
        }
        button {
          padding: 8px 20px;
          font-size: 12px;
          margin: 0 5px;
          cursor: pointer;
          border: 1px solid #ccc;
          background: white;
          border-radius: 5px;
          transition: all 0.2s;
        }
        button:hover { background: #e0e0e0; }

        @media print {
          body { padding: 0; }
          .controles { display: none; }
          .stat-card { break-inside: avoid; }
          @page { margin: 1.5cm; }
        }
      </style>
    </head>
    <body>
      <div class="reporte">
        <div class="header">
          <div class="logo-area">
            ${getLogoBase64() ? `<img src="data:image/jpeg;base64,${getLogoBase64()}" class="logo-img" onerror="this.style.display='none'">` : ''}
            <div>
              <div class="empresa-nombre">${empresa.nombre}</div>
              <div class="empresa-slogan">${empresa.slogan}</div>
            </div>
          </div>
          <div class="rif-info">
            RIF: ${empresa.rif}<br>
            ${empresa.direccion || ''}<br>
            ${empresa.ciudad}
          </div>
        </div>

        <div class="titulo">
          <h1>${tituloReal}</h1>
          <div class="fecha">Generado: ${formatearFechaReporte(fechaActual.toISOString())}</div>
        </div>

        <div class="stats-container">
          <div class="stat-card">
            <div class="stat-label">Total Pedidos</div>
            <div class="stat-value">${estadisticas.total}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Completados</div>
            <div class="stat-value green">${estadisticas.completados}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Facturado USD</div>
            <div class="stat-value blue">$${formatearNumero(estadisticas.totalFacturadoUsd)}</div>
          </div>
        </div>

        <div class="tabla-container">
          ${pedidosNorm.length > 0 ? `
            <table>
              <thead>
                <tr>
                  <th>N° Pedido</th>
                  <th>Fecha</th>
                  <th>Cliente</th>
                  <th>Zona</th>
                  <th class="text-center">Estado</th>
                  <th class="text-center">Método Pago</th>
                  <th class="text-center">Cant.</th>
                  <th class="text-center">Kilos</th>
                  <th class="text-center">Litros</th>
                  <th class="text-right">Total USD</th>
                </tr>
              </thead>
              <tbody>
                ${pedidosNorm.map(p => {
                  const badgeEstado = getBadgeEstadoPedido(p.estado);
                  const badgeMetodo = getBadgeMetodoPago(p.metodoPago);
                  return `
                    <tr>
                      <td><strong>${p.numeroPedido}</strong></td>
                      <td>${formatearFechaCorta(p.fechaPedido)}</td>
                      <td>${p.nombreCliente}</td>
                      <td>${p.nombreZona}</td>
                      <td class="text-center"><span class="${badgeEstado.clase}">${badgeEstado.texto}</span></td>
                      <td class="text-center"><span class="${badgeMetodo.clase}">${badgeMetodo.texto}</span></td>
                      <td class="text-center"><strong>${p.cantidadTotal}</strong></td>
                      <td class="text-center"><strong>${formatearNumero(p.kilosTotales, 1)} kg</strong></td>
                      <td class="text-center"><strong>${formatearNumero(p.litrosTotales, 1)} L</strong></td>
                      <td class="text-right"><strong>$ ${formatearNumero(p.totalUsd)}</strong></td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
              <tfoot style="background: #f5f5f5; font-weight: bold; border-top: 2px solid #333;">
                <tr>
                  <td colspan="6" class="text-right">TOTALES:</td>
                  <td class="text-center">${formatearNumero(estadisticas.totalCantidad, 0)}</td>
                  <td class="text-center">${formatearNumero(estadisticas.totalKilos, 1)} kg</td>
                  <td class="text-center">${formatearNumero(estadisticas.totalLitros, 1)} L</td>
                  <td class="text-right">$ ${formatearNumero(estadisticas.totalFacturadoUsd)}</td>
                </tr>
              </tfoot>
            </table>
          ` : '<div style="padding: 40px; text-align: center; border: 1px solid #ddd; border-radius: 8px;">No hay pedidos para mostrar</div>'}
        </div>

        <div class="footer">
          ${empresa.nombre} - Sistema de Gestión de Recarga de Gas
        </div>
      </div>

      <div class="controles">
        <button onclick="window.print()">🖨️ Imprimir Reporte</button>
        <button onclick="window.close()">✖️ Cerrar</button>
      </div>
    </body>
    </html>
  `;
};

// ==================== IMPRIMIR ====================
export const imprimirReporteGas = async (pedidos, titulo = null, filtro = 'todos', pagos = []) => {
  if (!pedidos || pedidos.length === 0) {
    alert('No hay pedidos para mostrar');
    return false;
  }
  try {
    LOGO_BASE64 = '';
    await cargarLogo();

    const htmlReporte = generarHTMLReporteGas(pedidos, titulo, filtro, pagos);
    const ventana = window.open('', '_blank', 'width=1200,height=700,menubar=yes,toolbar=yes,location=yes,status=yes,scrollbars=yes');

    if (!ventana) {
      alert('Por favor, permite ventanas emergentes para generar el reporte');
      return false;
    }

    ventana.document.write(htmlReporte);
    ventana.document.close();
    ventana.focus();
    return true;
  } catch (error) {
    console.error('Error generando reporte:', error);
    alert('Error al generar el reporte: ' + error.message);
    return false;
  }
};

// ==================== EXCEL ====================
export const exportarPedidosGasExcel = (pedidos, titulo = 'pedidos_gas', filtro = 'todos', pagos = []) => {
  try {
    const pedidosNorm = filtrarPedidosGas(pedidos, filtro, pagos);
    const XLSX = require('xlsx');
    const wb = XLSX.utils.book_new();

    const data = pedidosNorm.map(p => ({
      'N° Pedido': p.numeroPedido,
      'Fecha': formatearFechaCorta(p.fechaPedido),
      'Cliente': p.nombreCliente,
      'Cédula': p.cedulaCliente,
      'Teléfono': p.telefonoCliente,
      'Zona': p.nombreZona,
      'Dirección': p.direccionEntrega,
      'Estado': p.estado,
      'Estado Pago': p.estadoPago,
      'Método Pago': p.metodoPago || '—',
      'Cantidad': p.cantidadTotal,
      'Kilos': p.kilosTotales,
      'Litros': p.litrosTotales,
      'Subtotal USD': p.subtotalUsd,
      'Delivery USD': p.costoDeliveryUsd,
      'IVA USD': p.ivaUsd,
      'Descuento USD': p.descuentoUsd,
      'Total USD': p.totalUsd,
      'Total Bs': p.totalBs,
      'Tasa Bs/USD': p.tasaBsUsd,
      'Comisión USD': calcularComisionPedido(p)
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [
      { wch: 18 }, { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 15 },
      { wch: 18 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
      { wch: 10 }, { wch: 10 }, { wch: 10 },
      { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 },
      { wch: 15 }, { wch: 12 }, { wch: 12 }
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Pedidos Gas');

    const fechaStr = new Date().toISOString().split('T')[0];
    const nombreArchivo = `pedidos_gas_${titulo}_${fechaStr}.xlsx`;
    XLSX.writeFile(wb, nombreArchivo);

    return { success: true, filename: nombreArchivo, count: pedidosNorm.length };
  } catch (error) {
    console.error('Error exportando a Excel:', error);
    return { success: false, error: error.message };
  }
};

export default {
  SYSTEM_JJ_CONFIG,
  cargarLogo,
  getLogoBase64,
  formatearFechaReporte,
  formatearFechaCorta,
  formatearNumero,
  formatearMonedaUSD,
  formatearMonedaBs,
  normalizarPedidoGas,
  normalizarPedidosGas,
  getBadgeEstadoPedido,
  getBadgeEstadoPago,
  getBadgeMetodoPago,
  calcularEstadisticasGas,
  filtrarPedidosGas,
  generarHTMLReporteGas,
  imprimirReporteGas,
  exportarPedidosGasExcel
};